// CB:BEGIN admin-marketplace-health
import React, { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from '../../lib/useAuthFetch';
import { getApiUrl } from '../../lib/getApiUrl';
import MetricCard from '../ui/MetricCard';

interface HealthSnapshot {
  projectsOpen: number;
  contractorsActive: number;
  medianTimeToFirstBidHours: number | null;
  averageBidsPerProject: number;
  projectsWithoutBid24h: number;
  capturedAt: string;
}

const HEALTH_STATUS = (snapshot: HealthSnapshot) => {
  if (snapshot.projectsWithoutBid24h > snapshot.projectsOpen * 0.3)
    return { label: 'Under Supply', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
  if (snapshot.averageBidsPerProject < 1.5)
    return { label: 'Low Competition', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' };
  return { label: 'Healthy', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
};

const AdminMarketplaceHealth: React.FC = () => {
  const authFetch = useAuthFetch();
  const [snapshot, setSnapshot] = useState<HealthSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const res = await authFetch(getApiUrl('/api/admin/marketplace-health'));
      if (res.ok) {
        const data = await res.json();
        setSnapshot(data.snapshot || null);
      } else {
        setError('Failed to load marketplace health data.');
      }
    } catch {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const supplyDemandRatio = snapshot && snapshot.contractorsActive > 0
    ? (snapshot.contractorsActive / Math.max(snapshot.projectsOpen, 1)).toFixed(1)
    : null;

  const healthStatus = snapshot ? HEALTH_STATUS(snapshot) : null;

  if (loading) {
    return (
      <div style={{ padding: '28px 32px', maxWidth: 960, margin: '0 auto' }}>
        <div style={{ height: 32, width: 200, background: '#f1f5f9', borderRadius: 8, marginBottom: 16 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[1,2,3,4,5,6].map(i => (
            <div key={i} style={{ height: 90, background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', maxWidth: 960, margin: '0 auto', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Marketplace Health</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            Live supply/demand metrics
            {snapshot && <span style={{ color: '#94a3b8' }}> — captured {new Date(snapshot.capturedAt).toLocaleString()}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {healthStatus && (
            <span style={{
              padding: '5px 14px', borderRadius: 99,
              background: healthStatus.bg, color: healthStatus.color,
              border: `1px solid ${healthStatus.border}`,
              fontWeight: 700, fontSize: 12,
            }}>
              {healthStatus.label}
            </span>
          )}
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            style={{ padding: '7px 16px', background: '#f9fafb', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 500, opacity: refreshing ? 0.6 : 1 }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#dc2626', fontSize: 13, marginBottom: 24 }}>
          {error}
        </div>
      )}

      {snapshot && (
        <>
          {/* ── KPI grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 28 }}>
            <MetricCard
              value={snapshot.projectsOpen}
              label="Open Projects"
              sub="Accepting bids"
              accent="#2563eb"
              accentBg="#eff6ff"
              icon={<svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" /></svg>}
            />
            <MetricCard
              value={snapshot.contractorsActive}
              label="Active Contractors"
              sub="Stripe payout enabled"
              accent="#7c3aed"
              accentBg="#f5f3ff"
              icon={<svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
            />
            <MetricCard
              value={snapshot.averageBidsPerProject.toFixed(1)}
              label="Avg Bids / Project"
              sub="Last 30 days"
              accent={snapshot.averageBidsPerProject >= 2 ? '#16a34a' : '#ea580c'}
              accentBg={snapshot.averageBidsPerProject >= 2 ? '#f0fdf4' : '#fff7ed'}
              icon={<svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
            />
            <MetricCard
              value={snapshot.medianTimeToFirstBidHours != null ? `${snapshot.medianTimeToFirstBidHours}h` : 'N/A'}
              label="Median Time to First Bid"
              sub="Last 30 days"
              accent="#0891b2"
              accentBg="#ecfeff"
              icon={<svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            />
            <MetricCard
              value={snapshot.projectsWithoutBid24h}
              label="No Bids After 24h"
              sub="Open projects lacking bids"
              accent={snapshot.projectsWithoutBid24h === 0 ? '#16a34a' : '#dc2626'}
              accentBg={snapshot.projectsWithoutBid24h === 0 ? '#f0fdf4' : '#fef2f2'}
              icon={<svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
            />
            <MetricCard
              value={supplyDemandRatio ?? 'N/A'}
              label="Supply / Demand Ratio"
              sub="Contractors per open project"
              accent="#374151"
              accentBg="#f8fafc"
              icon={<svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
            />
          </div>

          {/* ── Operational guidance ── */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 14 }}>Operational Guidance</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {snapshot.projectsWithoutBid24h > 0 && (
                <div style={{ display: 'flex', gap: 12, padding: '12px 16px', background: '#fef2f2', borderRadius: 8, borderLeft: '3px solid #dc2626' }}>
                  <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>Action needed</span>
                  <span style={{ color: '#374151', fontSize: 13, lineHeight: 1.5 }}>
                    {snapshot.projectsWithoutBid24h} open project{snapshot.projectsWithoutBid24h > 1 ? 's have' : ' has'} no bids after 24 hours — consider contractor outreach or liquidity run.
                  </span>
                </div>
              )}
              {snapshot.averageBidsPerProject < 1.5 && (
                <div style={{ display: 'flex', gap: 12, padding: '12px 16px', background: '#fff7ed', borderRadius: 8, borderLeft: '3px solid #ea580c' }}>
                  <span style={{ color: '#ea580c', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>Low competition</span>
                  <span style={{ color: '#374151', fontSize: 13, lineHeight: 1.5 }}>
                    Average bids per project is {snapshot.averageBidsPerProject.toFixed(1)}. Recruit more active contractors or expand service areas.
                  </span>
                </div>
              )}
              {snapshot.projectsWithoutBid24h === 0 && snapshot.averageBidsPerProject >= 2 && (
                <div style={{ display: 'flex', gap: 12, padding: '12px 16px', background: '#f0fdf4', borderRadius: 8, borderLeft: '3px solid #16a34a' }}>
                  <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>Healthy</span>
                  <span style={{ color: '#374151', fontSize: 13, lineHeight: 1.5 }}>
                    Supply and demand are balanced. All open projects have received bids within 24 hours.
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminMarketplaceHealth;
// CB:END admin-marketplace-health
